/**
 * Helpers para parsing do endereço retornado pelo Traccar.
 *
 * O Traccar normalmente retorna o endereço no formato:
 *   "Rua X, 123, Cidade, CEP, Estado por extenso, BR"
 * Exemplo: "Rua Mipibu, Natal, 59020-090, Rio Grande do Norte, BR"
 *
 * Convertemos o nome do estado para UF (sigla) para permitir filtros.
 */

const STATE_NAME_TO_UF: Record<string, string> = {
  acre: 'AC',
  alagoas: 'AL',
  amapa: 'AP',
  amapá: 'AP',
  amazonas: 'AM',
  bahia: 'BA',
  ceara: 'CE',
  ceará: 'CE',
  'distrito federal': 'DF',
  'espirito santo': 'ES',
  'espírito santo': 'ES',
  goias: 'GO',
  goiás: 'GO',
  maranhao: 'MA',
  maranhão: 'MA',
  'mato grosso': 'MT',
  'mato grosso do sul': 'MS',
  'minas gerais': 'MG',
  para: 'PA',
  pará: 'PA',
  paraiba: 'PB',
  paraíba: 'PB',
  parana: 'PR',
  paraná: 'PR',
  pernambuco: 'PE',
  piaui: 'PI',
  piauí: 'PI',
  'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN',
  'rio grande do sul': 'RS',
  rondonia: 'RO',
  rondônia: 'RO',
  roraima: 'RR',
  'santa catarina': 'SC',
  'sao paulo': 'SP',
  'são paulo': 'SP',
  sergipe: 'SE',
  tocantins: 'TO',
};

const VALID_UFS = new Set(Object.values(STATE_NAME_TO_UF));

/** UFs em ordem alfabética. */
export const ALL_UFS = Array.from(VALID_UFS).sort();

/**
 * Extrai a UF (sigla de 2 letras) do endereço. Estratégia:
 * 1. Se houver "XX, BR" no fim ou em algum lugar, devolve XX direto.
 * 2. Procura nome de estado por extenso (case-insensitive) no texto.
 * 3. Retorna null se não conseguir identificar.
 */
export function extractUF(address?: string | null): string | null {
  if (!address) return null;
  const trimmed = address.trim();
  if (!trimmed) return null;

  // 1. UF de 2 letras isolada por vírgulas
  const ufMatch = trimmed.match(/(?:^|,\s*)([A-Z]{2})(?=\s*(?:,|$))/);
  if (ufMatch) {
    const uf = ufMatch[1];
    if (VALID_UFS.has(uf)) return uf;
  }

  // 2. Nome do estado por extenso
  const lower = trimmed.toLowerCase();
  for (const [name, uf] of Object.entries(STATE_NAME_TO_UF)) {
    // usa boundary de vírgula ou início/fim para evitar matches em rua/cidade
    const re = new RegExp(`(?:^|,\\s*)${escapeRegExp(name)}(?=\\s*(?:,|$))`);
    if (re.test(lower)) return uf;
  }
  return null;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Limpa o endereço retornado pelo Traccar para o formato NautMax:
 *   "rua, número, bairro, cidade, UF, PAÍS"
 *
 * - Remove CEP brasileiro (XXXXX-XXX ou XXXXXXXX)
 * - Substitui nome do estado por extenso pela sigla UF
 *   (ex: "Sergipe" → "SE")
 * - Normaliza espaços e vírgulas duplicadas
 */
export function formatAddress(address?: string | null): string {
  if (!address) return '';
  let cleaned = address.trim();

  // Remove CEP: padrões "XXXXX-XXX" ou "XXXXXXXX" entre vírgulas ou no início/fim
  cleaned = cleaned.replace(/\b\d{5}-?\d{3}\b/g, '');

  // Substitui nome do estado por extenso pela UF
  const segments = cleaned.split(',').map((s) => s.trim()).filter(Boolean);
  const finalSegments = segments.map((seg) => {
    const lower = seg.toLowerCase();
    for (const [name, uf] of Object.entries(STATE_NAME_TO_UF_LOOKUP)) {
      if (lower === name) return uf;
    }
    return seg;
  });

  return finalSegments.join(', ');
}

// Lookup interno (mesmo conteúdo do STATE_NAME_TO_UF mas como const exportada parcialmente)
const STATE_NAME_TO_UF_LOOKUP: Record<string, string> = {
  acre: 'AC',
  alagoas: 'AL',
  amapa: 'AP',
  amapá: 'AP',
  amazonas: 'AM',
  bahia: 'BA',
  ceara: 'CE',
  ceará: 'CE',
  'distrito federal': 'DF',
  'espirito santo': 'ES',
  'espírito santo': 'ES',
  goias: 'GO',
  goiás: 'GO',
  maranhao: 'MA',
  maranhão: 'MA',
  'mato grosso': 'MT',
  'mato grosso do sul': 'MS',
  'minas gerais': 'MG',
  para: 'PA',
  pará: 'PA',
  paraiba: 'PB',
  paraíba: 'PB',
  parana: 'PR',
  paraná: 'PR',
  pernambuco: 'PE',
  piaui: 'PI',
  piauí: 'PI',
  'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN',
  'rio grande do sul': 'RS',
  rondonia: 'RO',
  rondônia: 'RO',
  roraima: 'RR',
  'santa catarina': 'SC',
  'sao paulo': 'SP',
  'são paulo': 'SP',
  sergipe: 'SE',
  tocantins: 'TO',
};
